<template>
  <el-dialog
    class="ai-chat-overlay"
    title="ICEChat"
    top="0"
    :visible="visible"
    :width="dialogWidth"
    :modal="false"
    :lock-scroll="false"
    :close-on-click-modal="false"
    :show-close="false"
    :append-to-body="true"
    :custom-class="expanded ? 'ai-chat-dialog is-expanded' : 'ai-chat-dialog'"
    :before-close="handleClose"
  >
    <slot></slot>
    <!-- 左边缘拖动条：窗口靠右停靠，向左拖动变宽；也支持左右方向键微调 -->
    <div
      class="resize-handle"
      role="separator"
      aria-orientation="vertical"
      :aria-label="$t('aiChatDialog.tzckkd')"
      tabindex="0"
      @mousedown.prevent="onResizeStart"
      @keydown.left.prevent="onResizeByKey(-16)"
      @keydown.right.prevent="onResizeByKey(16)"
      @keyup="saveResizedWidth"
    ></div>
  </el-dialog>
</template>

<script>
// 对话框默认宽度（样式上的初始宽度），同时作为拖动的最小宽度
const DEFAULT_WIDTH = 414;
// 视口留白与样式保持一致（上/右各 16px），最大宽度 = 视口宽度 - 32px
const VIEWPORT_GAP = 32;
// 宽度缓存 key：记录用户调整后的宽度，下次打开作为默认宽度
const STORAGE_KEY = 'aiChatDialogWidth';

// 读取本地保存的宽度；小于最小宽度或超出当前视口最大宽度的历史值视为无效，返回 0 表示无有效值
function getStoredWidth(minWidth, maxWidth) {
  const stored = parseInt(localStorage.getItem(STORAGE_KEY), 10);
  return stored >= minWidth && stored <= maxWidth ? stored : 0;
}

// 将宽度限制在最小/最大宽度之间
function clampWidth(width, minWidth, maxWidth) {
  return Math.min(Math.max(width, minWidth), maxWidth);
}

export default {
  name: 'AiChatDialog',
  props: {
    visible: { type: Boolean, default: false },
    width: { type: String, default: `${DEFAULT_WIDTH}px` },
    expanded: { type: Boolean, default: false },
  },
  data() {
    const minWidth = parseInt(this.width, 10) || DEFAULT_WIDTH;
    return {
      resizedWidth: 0, // 本次拖动后的宽度（px），0 表示未拖动
      // 本地保存的宽度（px），0 表示未保存过；作为下次打开时的默认宽度
      savedWidth: getStoredWidth(minWidth, window.innerWidth - VIEWPORT_GAP),
    };
  },
  computed: {
    // 最小宽度：对话框原本打开时的样式宽度，拖动只能在此基础上变大
    minWidth() {
      return parseInt(this.width, 10) || DEFAULT_WIDTH;
    },
    // 实际宽度（px 字符串）：本次拖动值 > 本地保存值 > 原始样式宽度
    dialogWidth() {
      return `${this.resizedWidth || this.savedWidth || this.minWidth}px`;
    },
  },
  watch: {
    // 关闭后清除本次拖动宽度，下次打开从保存的宽度（或默认宽度）开始
    visible(value) {
      if (!value) {
        this.resizedWidth = 0;
      }
    },
  },
  methods: {
    handleClose() {
      this.$emit('close', false);
    },
    // 最大宽度与“展开窗口”保持一致：视口宽度减去两侧留白
    getMaxWidth() {
      const viewportWidth = (this.$el && this.$el.clientWidth) || window.innerWidth;
      return viewportWidth - VIEWPORT_GAP;
    },
    // 按最小/最大宽度截断后记录本次拖动宽度
    setWidth(width) {
      this.resizedWidth = clampWidth(width, this.minWidth, this.getMaxWidth());
    },
    // 拖动/键盘调整结束后，只有宽度大于最小宽度才记录，作为下次打开的默认宽度
    saveResizedWidth() {
      if (this.resizedWidth > this.minWidth) {
        localStorage.setItem(STORAGE_KEY, this.resizedWidth);
        this.savedWidth = this.resizedWidth;
      }
    },
    // 展开状态下宽度被样式锁定，调整前先退出展开态
    collapseIfExpanded() {
      if (this.expanded) {
        this.$emit('update:expanded', false);
      }
    },
    // 鼠标拖动：记录起点，移动时按“向左变宽、向右变窄”计算并截断宽度
    onResizeStart(e) {
      this.collapseIfExpanded();
      const startWidth = this.$el.querySelector('.el-dialog').offsetWidth;
      const startX = e.clientX;
      const onMove = (event) => {
        this.setWidth(startWidth + (startX - event.clientX));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        // 拖动结束保存宽度，下次打开作为默认宽度
        this.saveResizedWidth();
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      // 拖动期间禁止选中文字，并保持 col-resize 光标
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    },
    // 键盘微调：左右方向键每次调整 16px，松开按键后由 keyup 保存
    onResizeByKey(step) {
      this.collapseIfExpanded();
      this.setWidth(this.$el.querySelector('.el-dialog').offsetWidth + step);
    },
  },
};
</script>

<style lang="scss">
// 保留业务页面的操作空间，弹窗内仅由聊天组件绘制标题栏。
.ai-chat-overlay {
  pointer-events: none;
  overflow: hidden;

  .ai-chat-dialog {
    position: absolute;
    top: 16px;
    right: 16px;
    display: flex;
    flex-direction: column;
    height: calc(100% - 32px);
    max-height: 1066px;
    max-width: calc(100% - 32px);
    margin: 0;
    overflow: hidden;
    // 对齐设计稿：圆角 15 + 单层阴影
    border-radius: 15px !important;
    background: #ffffff;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.06);
    pointer-events: auto;
    &.is-expanded { width: calc(100% - 32px) !important; }
    .el-dialog__header { display: none; }
    .el-dialog__body { flex: 1; min-height: 0; padding: 0; }
  }

  // 左边缘拖动条：8px 热区，悬停/聚焦时才显示提示线，避免破坏原有设计
  .resize-handle {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 10;
    width: 8px;
    height: 100%;
    cursor: col-resize;

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 3px;
      width: 2px;
      height: 32px;
      border-radius: 1px;
      background: rgba(0, 0, 0, 0.16);
      opacity: 0;
      transform: translateY(-50%);
      transition: opacity 0.2s ease;
    }
    &:hover::after,
    &:focus-visible::after { opacity: 1; }
    &:focus-visible {
      outline: none;
      &::after { background: #2563eb; opacity: 1; }
    }
  }
}
@media (max-width: 600px) {
  .ai-chat-overlay .ai-chat-dialog {
    top: 8px;
    right: 8px;
    height: calc(100% - 16px);
    max-width: calc(100% - 16px);
    width: calc(100% - 16px) !important;
  }
}
</style>
